import React from 'react';
import { call, all, put, take, select, race } from 'redux-saga/effects';
import { request } from '../../xhr';
import { actions as StreamActions } from '../../Stream';
import { getCurrentStreamId, getCurrentGallection, getCurrentSubfolder, getCurrentMediaSource, getCurrentTier } from '../selectors';
import { deselectMulti } from '../actions';
import updateGallectionSize from './updateGallectionSize';
import { isPremiumGallection, getPremiumGallectionType } from '../../utils/gallections';
import { pushGlobalConfirmationModalForMM, confirmYes, confirmNo } from '../../Modals/redux/actionCreators';
import { PremiumTrash, TrashCan } from '@wix/da-ds/pkg/Icons/Confirmations';
import { GallectionType } from '../../types/gallection';
import { MediaSourceType } from '../types';
import { getDeviationEntityIds } from '../../redux/normalizr/schemas/deviation';
import { deleteDeviation } from '../../redux/deviations/actionCreators';
function* doConfirmationIfNeeded({ mediaSourceType , currentGallection , deviations  }) {
    if (mediaSourceType === MediaSourceType.CUSTOM_SET) {
        return true;
    }
    const isTierContent = [
        MediaSourceType.TIER_DEVIATIONS,
        MediaSourceType.TIER_POSTS
    ].includes(mediaSourceType);
    const folderType = isTierContent ? 'tier' : getPremiumGallectionType(currentGallection) || 'regular';
    let titleTransKey, textTransKey;
    switch(folderType){
        case 'tier':
            titleTransKey = deviations.length > 1 ? 'mediapopup.confirmationPopup.removeDeviation.title.tier.multi' : 'mediapopup.confirmationPopup.removeDeviation.title.tier';
            textTransKey = deviations.length > 1 ? 'mediapopup.confirmationPopup.removeDeviation.text.tier.multi' : 'mediapopup.confirmationPopup.removeDeviation.text.tier';
            break;
        case 'paid':
            titleTransKey = deviations.length > 1 ? 'mediapopup.confirmationPopup.removeDeviation.title.paid.multi' : 'mediapopup.confirmationPopup.removeDeviation.title.paid';
            textTransKey = deviations.length > 1 ? 'mediapopup.confirmationPopup.removeDeviation.text.paid.multi' : 'mediapopup.confirmationPopup.removeDeviation.text.paid';
            break;
        case 'watchers':
            titleTransKey = deviations.length > 1 ? 'mediapopup.confirmationPopup.removeDeviation.title.watchers.multi' : 'mediapopup.confirmationPopup.removeDeviation.title.watchers';
            textTransKey = deviations.length > 1 ? 'mediapopup.confirmationPopup.removeDeviation.text.watchers.multi' : 'mediapopup.confirmationPopup.removeDeviation.text.watchers';
            break;
        default:
            titleTransKey = deviations.length > 1 ? 'mediapopup.confirmationPopup.removeDeviation.title.multi' : 'mediapopup.confirmationPopup.removeDeviation.title';
            switch(currentGallection.type){
                case GallectionType.STASH:
                    textTransKey = deviations.length > 1 ? 'mediapopup.confirmationPopup.removeDeviation.text.stash.multi' : 'mediapopup.confirmationPopup.removeDeviation.text.stash';
                    break;
                case GallectionType.COLLECTION:
                    textTransKey = deviations.length > 1 ? 'mediapopup.confirmationPopup.removeDeviation.text.collection.multi' : 'mediapopup.confirmationPopup.removeDeviation.text.collection';
                    break;
                case GallectionType.GALLERY:
                default:
                    textTransKey = deviations.length > 1 ? 'mediapopup.confirmationPopup.removeDeviation.text.gallery.multi' : 'mediapopup.confirmationPopup.removeDeviation.text.gallery';
            }
            break;
    }
    if (isPremiumGallection(currentGallection)) {
        yield put(pushGlobalConfirmationModalForMM({
            titleTransKey,
            textTransKey,
            confirmBtnLabelTransKey: 'mediapopup.confirmationPopup.removeDeviation.confirm',
            cancelBtnLabelTransKey: 'popup.cancel',
            confirmBtnType: 'danger',
            icon: /*#__PURE__*/ React.createElement(PremiumTrash, null)
        }));
    } else {
        yield put(pushGlobalConfirmationModalForMM({
            titleTransKey,
            textTransKey,
            confirmBtnLabelTransKey: 'mediapopup.itemMenu.delete',
            cancelBtnLabelTransKey: 'popup.cancel',
            confirmBtnType: 'danger',
            icon: /*#__PURE__*/ React.createElement(TrashCan, null)
        }));
    }
    const { yes: isConfirmed  } = yield race({
        yes: take(confirmYes),
        no: take(confirmNo)
    });
    return !!isConfirmed;
}
function* removeDeviationsHandler(action) {
    const deviations = action.payload;
    const { streamId , currentGallection , currentTier , mediaSourceType , currentSubfolder  } = yield select((state)=>({
            mediaSourceType: getCurrentMediaSource(state),
            streamId: getCurrentStreamId(state),
            currentGallection: getCurrentGallection(state),
            currentTier: getCurrentTier(state),
            currentSubfolder: getCurrentSubfolder(state)
        }));
    const confirmed = yield call(doConfirmationIfNeeded, {
        mediaSourceType,
        currentGallection,
        deviations
    });
    if (!confirmed) {
        return;
    }
    const deviationEntityIds = getDeviationEntityIds(deviations);
    yield all([
        put(StreamActions.deleteItems({
            streamId,
            items: deviationEntityIds
        })),
        put(deselectMulti(deviations))
    ]);
    if (currentGallection) {
        const type = currentGallection.type;
        // deleting from a stash folder deletes the deviation as a whole
        if (type === 'stash') {
            yield all(deviations.map((deviation)=>put(deleteDeviation({
                    deviation
                }))));
            return;
        }
        const currentFolderId = currentSubfolder ? currentSubfolder.folderId : currentGallection.folderId;
        yield call(updateGallectionSize, currentFolderId, -deviations.length);
        // TODO: should this endpoint accept typeId as well?
        const deviationIds = deviations.map((deviation)=>deviation.deviationId);
        const data = {
            type,
            folderid: currentFolderId,
            deviationids: deviationIds
        };
        yield call(request, {
            method: 'post',
            url: 'gallection/folders/remove_deviations',
            data
        });
    }
    if (currentTier) {
        const tierId = currentTier.deviationId;
        // TODO: should this endpoint accept typeId as well?
        const deviationIds1 = deviations.map((deviation)=>deviation.deviationId);
        const data1 = {
            tier_deviationid: tierId,
            deviationids: deviationIds1
        };
        yield call(request, {
            method: 'post',
            url: 'tier/deviation/remove',
            data: data1
        });
    }
}
export default removeDeviationsHandler;
